import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  getAgentAnnouncements as fetchAgentAnnouncementsApi,
  acknowledgeSopAnnouncements
} from '../../Redux/util/Apis/sopAndAnnouncements';
import { socketEmitter, socketListener, socketRemover } from '../socket';
import { GlobalContext } from './GlobalContextProvider';
import _ from 'lodash';

const InboxContext = createContext({});
export const useInboxContext = () => useContext(InboxContext);

const InboxContextProvider = ({ children }) => {
  const [scheduledMessages, setScheduledMessages] = useState(null);
  const [agentAnnouncements, setAgentAnnouncements] = useState([]);
  const [agentAnnouncementsLoading, setAgentAnnouncementsLoading] =
    useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcementsByCommunity, setAnnouncementsByCommunity] = useState([]);
  const [leadMessage, setLeadMessage] = useState([]);
  const [lastIncomingMessages, setLastIncomingMessages] = useState([]);
  const [isUpdatingMessageList, setIsUpdatingMessageList] = useState(false);

  const { userData } = useContext(GlobalContext);
  const storeAnnouncementsByCommunity = id =>
    setAnnouncementsByCommunity([...id]);

  const fetchAgentAnnouncements = useCallback(async () => {
    setAgentAnnouncementsLoading(true);
    const result = await fetchAgentAnnouncementsApi();
    setAgentAnnouncementsLoading(false);
    if (!result?.error) {
      setAgentAnnouncements(result?.data ?? []);
    }
    return result;
  }, []);

  const sopAcknowledgementByAgent = useCallback(async () => {
    if (!announcementsByCommunity?.length) return;
    const result = await acknowledgeSopAnnouncements(announcementsByCommunity);
    if (!result?.error) {
      await fetchAgentAnnouncements();
    }
    return result;
  }, [announcementsByCommunity, fetchAgentAnnouncements]);

  const toggleAnnouncementsModalVisibility = updatedState => {
    setShowAnnouncementsModal(updatedState);
  };

  // Debounced incoming-message handler.
  //
  // ZUM-2479: the apis-side SQS consumer no longer throttles `messages-${leadId}`
  // emits (legacy paced them at one per 5s; we dropped that for durability). If
  // a tenant fires a burst of inbound texts, we'll receive the corresponding
  // burst of refresh emits within tens of milliseconds. Without this debounce,
  // each emit drives a React re-render of the inbox view — visible jank during
  // bursty traffic.
  //
  // leading + trailing means the first emit in a burst still renders
  // immediately (no perceived lag), subsequent emits during the 250ms window
  // are collapsed, and the final state after the burst gets one render.
  const debouncedUpdateRef = useRef(null);
  if (!debouncedUpdateRef.current) {
    debouncedUpdateRef.current = _.debounce(
      (leadId, messages) => {
        setLeadMessage(prev => {
          const unreadMessages = _.differenceBy(
            [...messages],
            [...prev],
            '_id'
          );
          if (unreadMessages?.length) {
            setIsUpdatingMessageList(true);
            const lastMessages = [];
            for (let i = messages?.length - 1; i >= 0; i--) {
              messages[i].isUnread =
                messages[i]?._id === unreadMessages[0]?._id;

              if (messages[i]?.type === 'incoming') {
                lastMessages.push({
                  text: messages[i]?.body,
                  id: messages[i]?._id
                });
              } else {
                if (lastMessages?.length) {
                  setLastIncomingMessages([...lastMessages]);
                  break;
                } else if (messages[i]?.type === 'outgoing') {
                  setLastIncomingMessages([
                    {
                      text: messages[i]?.body,
                      id: messages[i]?._id,
                      isOutGoing: true
                    }
                  ]);
                  break;
                }
              }
            }
            setIsUpdatingMessageList(false);
            if (leadId) {
              return messages;
            }
          }

          return prev;
        });
      },
      250,
      { leading: true, trailing: true }
    );
  }

  useEffect(
    () => () => {
      debouncedUpdateRef.current?.cancel();
    },
    []
  );

  // Stable reference — does NOT depend on `leadMessage`. The inner setter uses
  // the functional form, so we don't need to capture stale state. Stable
  // identity also stops `socketListener` registrations from re-binding on
  // every render.
  const handleIncomingSocketData = useCallback((leadId, messages) => {
    debouncedUpdateRef.current?.(leadId, messages);
  }, []);

  // Stable handler reference so socketListener's dedup can recognize repeat
  // registrations (otherwise every render creates a new function identity
  // and stacks another listener).
  const handleIncomingSocketDataForScheduledMessages = useCallback(
    (leadId, messages) => {
      setScheduledMessages(messages);
    },
    []
  );

  // Tracks every leadId this provider has subscribed `messages-*` /
  // `scheduled-messages-*` listeners for, so we can clean them up if the
  // provider unmounts (or remounts in dev/strict mode). Without this, the
  // last viewed lead's listeners would survive an unmount.
  const subscribedLeadIdsRef = useRef(new Set());

  useEffect(() => {
    return () => {
      for (const leadId of subscribedLeadIdsRef.current) {
        socketRemover(`messages-${leadId}`, handleIncomingSocketData);
        socketRemover(
          `scheduled-messages-${leadId}`,
          handleIncomingSocketDataForScheduledMessages
        );
      }
      subscribedLeadIdsRef.current.clear();
    };
  }, [handleIncomingSocketData, handleIncomingSocketDataForScheduledMessages]);

  const fetchNewTimeLineEvents = leadId => {
    if (leadId) {
      socketEmitter('getLeadMessages', {
        leadId: leadId,
        user: userData
      });

      socketListener(`messages-${leadId}`, handleIncomingSocketData);
      subscribedLeadIdsRef.current.add(leadId);
    }
  };

  const fetchScheduledMessages = leadId => {
    if (leadId) {
      socketEmitter('getLeadScheduledMessages', {
        leadId: leadId,
        user: userData
      });
      socketListener(
        `scheduled-messages-${leadId}`,
        handleIncomingSocketDataForScheduledMessages
      );
      subscribedLeadIdsRef.current.add(leadId);
    }
  };
  const value = {
    agentAnnouncements,
    showAnnouncementsModal,
    agentAnnouncementsLoading,
    leadMessage,
    lastIncomingMessages,
    isUpdatingMessageList,
    toggleAnnouncementsModalVisibility,
    sopAcknowledgementByAgent,
    storeAnnouncementsByCommunity,
    fetchAgentAnnouncements,
    fetchNewTimeLineEvents,
    handleIncomingSocketData,
    scheduledMessages,
    setScheduledMessages,
    fetchScheduledMessages
  };

  return (
    <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
  );
};

export default InboxContextProvider;
